import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../../hooks/apiClient';
import { buildUnitsByUuid, mapInventoryArticlesToMaterialOptions } from '../utils/internalOrdersUtils';

const DEBOUNCE_MS = 500;
export const MIN_ARTICLE_SEARCH_LENGTH = 3;

const addUniqueArticles = (results, seen, articles = []) => {
  articles.forEach((article) => {
    if (!article?.uuid || seen.has(article.uuid)) return;
    seen.add(article.uuid);
    results.push(article);
  });
};

const searchArticles = async (term) => {
  const results = [];
  const seen = new Set();

  const [listResult, codeResult, externalCodeResult] = await Promise.allSettled([
    apiClient.get('inventory_articles', {
      params: {
        search: term,
        status: 'active',
        page: 1,
        per_page: 50,
      },
    }),
    apiClient.get(`inventory_articles/by-code/${encodeURIComponent(term)}`),
    apiClient.get(`inventory_articles/by-external-code/${encodeURIComponent(term)}`),
  ]);

  if (listResult.status === 'fulfilled') {
    addUniqueArticles(results, seen, listResult.value.data?.results || []);
  } else if (listResult.reason?.response?.status !== 400) {
    throw listResult.reason;
  }

  if (codeResult.status === 'fulfilled' && codeResult.value?.data) {
    addUniqueArticles(results, seen, [codeResult.value.data]);
  } else if (
    codeResult.status === 'rejected' &&
    codeResult.reason?.response?.status !== 404
  ) {
    throw codeResult.reason;
  }

  if (externalCodeResult.status === 'fulfilled' && externalCodeResult.value?.data) {
    addUniqueArticles(results, seen, [externalCodeResult.value.data]);
  } else if (
    externalCodeResult.status === 'rejected' &&
    externalCodeResult.reason?.response?.status !== 404
  ) {
    throw externalCodeResult.reason;
  }

  return results;
};

export const useInventoryArticleSearch = (units = [], enabled = true) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');

  useEffect(() => {
    if (!enabled) {
      setSearchTerm('');
      setDebouncedTerm('');
    }
  }, [enabled]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm.trim());
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const canSearch =
    enabled && debouncedTerm.length >= MIN_ARTICLE_SEARCH_LENGTH;

  const { data: articles = [], isFetching, isError } = useQuery({
    queryKey: ['inventory_articles', 'search', debouncedTerm],
    queryFn: () => searchArticles(debouncedTerm),
    enabled: canSearch,
    staleTime: 60 * 1000,
    retry: false,
  });

  const unitsByUuid = buildUnitsByUuid(units);
  const materialOptions = mapInventoryArticlesToMaterialOptions(articles, unitsByUuid);

  const resetSearch = () => {
    setSearchTerm('');
    setDebouncedTerm('');
  };

  const trimmedSearch = searchTerm.trim();
  const isPendingDebounce =
    trimmedSearch.length >= MIN_ARTICLE_SEARCH_LENGTH &&
    trimmedSearch !== debouncedTerm;

  return {
    searchTerm,
    setSearchTerm,
    materialOptions,
    isSearching: isFetching && canSearch,
    isPendingDebounce,
    hasSearchError: isError,
    resetSearch,
    minSearchLength: MIN_ARTICLE_SEARCH_LENGTH,
  };
};
